<script>
import { iTextInput, showToast } from '@inscribe/signal';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import DecisionEngineNav from '@/V2/decision-engine/DecisionEngineNav.vue';
import BlocklistDelete from '@/V2/decision-engine/blocklist/BlocklistDelete.vue';

export default {
  components: { BlocklistDelete, DecisionEngineNav, iTextInput },
  data() {
    return {
      editingBlock: null,
      blockToBeDeleted: null,
      block: null,
    };
  },
  computed: {
    isNew() {
      return !this.block?.id;
    },
    isBlockValid() {
      return (
        this.editingBlock?.name &&
        this.editingBlock.name.trim() !== '' &&
        this.editingBlock?.address?.raw &&
        this.editingBlock.address.raw.trim() !== ''
      );
    },
  },
  mounted() {
    this.block = this.getCurrentEditingItem();
    if (!this.block) {
      this.goToBlocklist();
    } else {
      this.editingBlock = { ...this.block };
    }
  },
  methods: {
    ...mapMutations(['addBlocklist']),
    ...mapGetters(['getCurrentEditingItem']),
    ...mapActions(['tryAddBlocklist', 'tryEditBlocklist']),
    onBackButtonClick() {
      this.goToBlocklist();
    },
    goToBlocklist() {
      this.$router.push({
        name: 'decision-engine-blocklist',
      });
    },
    onDelete() {
      if (this.isNew) {
        this.goToBlocklist();
      } else {
        this.blockToBeDeleted = this.block;
      }
    },
    onBlockDeleted() {
      this.goToBlocklist();
    },
    onValueChange(type, value) {
      if (type === 'name') {
        this.editingBlock.name = value;
      } else if (type === 'address') {
        this.editingBlock.address.raw = value;
      }
    },
    createNewBlockList() {
      this.tryAddBlocklist({
        name: this.editingBlock.name,
        address: this.editingBlock.address.raw,
      }).then((response) => {
        this.$store.commit('addBlocklist', response.data);
        showToast({
          text: 'Blocklist updated',
        });
        this.goToBlocklist();
      });
    },
    editExistingBlocList() {
      this.tryEditBlocklist({
        name: this.editingBlock.name,
        address: this.editingBlock.address.raw,
        id: this.editingBlock.id,
      })
        .then((response) => {
          this.$store.commit('updateGeneric', {
            objectsName: 'blocklist',
            updatedObject: response.data,
          });
          showToast({
            text: 'Blocklist updated',
          });
          this.goToBlocklist();
        })
        .catch((error) => {
          if (error.response !== undefined && error.response.status === 403) {
            showToast({
              title: 'Permission Denied',
              text: 'You do not have permission to delete this blocklist. Please contact your admin if you require this permission.',
              isError: true,
            });
          } else {
            showToast({
              text: error?.data?.message || 'Please try again later',
              isError: true,
            });
          }
        });
    },
    saveOrCreateBlock() {
      if (this.isNew) {
        this.createNewBlockList();
      } else {
        this.editExistingBlocList();
      }
    },
  },
};
</script>

<template>
  <div class="blocklist-edit edit-container d-flex flex-column i-gap-11">
    <decision-engine-nav
      class="edit-nav"
      category-name="Blocklist"
      :action-name="isNew ? 'New Blocklist' : 'Edit Blocklist'"
      :button-label="isNew ? 'Add to Blocklist' : 'Update'"
      :button-disabled="!isBlockValid"
      :item-name="block?.name || 'New Blocklist'"
      :name-editable="false"
      @on-back-button-click="onBackButtonClick"
      @on-delete="onDelete"
      @on-button-click="saveOrCreateBlock"
    ></decision-engine-nav>

    <!-- edit block -->
    <div class="decision-engine-page-content d-flex flex-column i-gap-11">
      <div
        class="edit-block d-flex flex-row i-radius-3xl i-px-11 i-pt-12 i-pb-14 i-mt-11 i-gap-"
      >
        <div class="description half d-flex flex-column i-gap-2">
          <div class="title i-text-heading-5 i-text-neutral-120">
            Blocklist details
          </div>
          <div class="subtitle i-text-body-2 i-text-neutral-110">
            Manage the details of fraudulent customers you wish to block
          </div>
        </div>
        <div class="inputs half d-flex flex-column i-gap-9">
          <i-text-input
            label="Name"
            :model-value="editingBlock?.name"
            placeholder="Customer Name"
            data-testid="blocklist-input-name"
            @update:model-value="(value) => onValueChange('name', value)"
          ></i-text-input>

          <i-text-input
            label="Address"
            :model-value="editingBlock?.address?.raw"
            placeholder="Customer Address"
            data-testid="blocklist-input-address"
            @update:model-value="(value) => onValueChange('address', value)"
          ></i-text-input>

          <i-text-input
            v-if="!isNew"
            label="Normalised Address"
            :model-value="block.address?.normalized"
            data-testid="blocklist-input-normalised-address"
            :disabled="true"
          ></i-text-input>
        </div>
      </div>
    </div>

    <!-- delete -->
    <blocklist-delete
      :block-to-be-deleted="blockToBeDeleted"
      @on-close="blockToBeDeleted = null"
      @on-delete="onBlockDeleted"
    ></blocklist-delete>
  </div>
</template>

<style lang="scss">
.blocklist-edit {
  .edit-block {
    border: 1px solid $light-neutral-70;

    .half {
      width: 50%;
    }
  }
}
</style>
